import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import { boardAPI } from '../../api';
import { useAuthStore } from '../../store';
import './Calendar.css';

interface CalendarEvent {
  id: number;
  date: string;
  title: string;
  content: string;
  color: string;
  is_global: boolean;
  author: number;
  author_name?: string;
}

const COLORS = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22'];

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

// ===== 일정 추가 모달 =====
const AddEventModal: React.FC<{
  date: string;
  isAdmin: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ date, isAdmin, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [color, setColor] = useState('#3498db');
  const [isGlobal, setIsGlobal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) { alert('제목을 입력해주세요.'); return; }
    setSubmitting(true);
    try {
      await boardAPI.createEvent({ date, title, content, color, is_global: isGlobal });
      onSuccess();
      onClose();
    } catch { alert('등록에 실패했습니다.'); }
    setSubmitting(false);
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={e => e.stopPropagation()}>
        <div className="cal-modal-header">
          <span>📅 일정 추가 — {date}</span>
          <button className="cal-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="cal-modal-body">
          <input className="form-control" placeholder="일정 제목"
            value={title} onChange={e => setTitle(e.target.value)}
            style={{ marginBottom: 10, fontSize: 14 }} />
          <textarea className="form-control" placeholder="내용 (선택)"
            value={content} onChange={e => setContent(e.target.value)}
            rows={4} style={{ resize: 'vertical', fontSize: 13, marginBottom: 10 }} />
          {/* 색상 선택 */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>색상</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  style={{
                    width: 26, height: 26, borderRadius: '50%', background: c, border: 'none',
                    cursor: 'pointer', outline: color === c ? `3px solid ${c}` : 'none',
                    outlineOffset: 2, transform: color === c ? 'scale(1.2)' : 'scale(1)',
                    transition: 'all 0.15s',
                  }} />
              ))}
            </div>
          </div>
          {/* 전체공개 (관리자만) */}
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={isGlobal} onChange={e => setIsGlobal(e.target.checked)} />
              🌐 전체 공개 (모든 회원에게 표시)
            </label>
          )}
        </div>
        <div className="cal-modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>취소</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== 날짜 상세 모달 =====
const DayDetailModal: React.FC<{
  date: string;
  events: CalendarEvent[];
  onClose: () => void;
  onAdd: () => void;
  onDelete: (id: number) => void;
  isAdmin: boolean;
  userId: number | null;
}> = ({ date, events, onClose, onAdd, onDelete, isAdmin, userId }) => (
  <div className="cal-modal-overlay" onClick={onClose}>
    <div className="cal-modal" onClick={e => e.stopPropagation()}>
      <div className="cal-modal-header">
        <span>📅 {date} 일정</span>
        <button className="cal-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="cal-modal-body">
        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            등록된 일정이 없습니다.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {events.map(ev => (
              <div key={ev.id} className="cal-event-detail">
                <div className="cal-event-dot" style={{ background: ev.color }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {ev.title}
                    {ev.is_global && <span className="cal-global-badge">전체</span>}
                  </div>
                  {ev.content && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{ev.content}</div>}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{ev.author_name}</div>
                </div>
                {/* 본인 글 또는 관리자만 삭제 가능 */}
                {(isAdmin || ev.author === userId) && (
                  <button className="cal-delete-btn" onClick={() => onDelete(ev.id)}>🗑️</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="cal-modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>닫기</button>
        <button className="btn btn-primary" onClick={onAdd}>+ 일정 추가</button>
      </div>
    </div>
  </div>
);

// ===== 메인 캘린더 페이지 =====
const CalendarPage: React.FC = () => {
  const { isLoggedIn, isAdmin, user } = useAuthStore();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addDate, setAddDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await boardAPI.calendar(year, month);
      setEvents(res.data);
    } catch { setEvents([]); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  // 이전/다음 달
  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
    setSelectedDate(null);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
    setSelectedDate(null);
  };

  // 삭제
  const handleDelete = async (id: number) => {
    if (!window.confirm('일정을 삭제하시겠습니까?')) return;
    try {
      await boardAPI.deleteEvent(id);
      await load();
      setSelectedDate(null);
    } catch { alert('삭제에 실패했습니다.'); }
  };

  // 캘린더 날짜 계산
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=일
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const getDateStr = (day: number) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const getEventsForDate = (day: number) =>
    events.filter(e => e.date === getDateStr(day));

  const selectedEvents = selectedDate ? events.filter(e => e.date === selectedDate) : [];

  return (
    <Layout>
      <div className="cal-page">
        {/* 헤더 */}
        <div className="cal-header">
          <div className="cal-header-left">
            <h2 className="cal-title">📅 캘린더</h2>
          </div>
          <div className="cal-nav">
            <button className="cal-nav-btn" onClick={prevMonth}>◀</button>
            <span className="cal-nav-label">{year}년 {month}월</span>
            <button className="cal-nav-btn" onClick={nextMonth}>▶</button>
            <button className="btn btn-ghost btn-sm" onClick={() => {
              setYear(today.getFullYear()); setMonth(today.getMonth() + 1);
            }}>오늘</button>
          </div>
          <div className="cal-header-right">
            {isLoggedIn && (
              <button className="btn btn-primary btn-sm" onClick={() => {
                setAddDate(todayStr); setShowAdd(true);
              }}>+ 일정 추가</button>
            )}
          </div>
        </div>

        {/* 캘린더 본체 */}
        <div className="cal-body">
          {loading ? (
            <div className="loading-spinner"><div className="spinner" /></div>
          ) : (
            <div className="cal-grid">
              {/* 요일 헤더 */}
              {WEEKDAYS.map((d, i) => (
                <div key={d} className={`cal-weekday ${i === 0 ? 'sun' : i === 6 ? 'sat' : ''}`}>{d}</div>
              ))}

              {/* 빈 칸 (월 시작 전) */}
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="cal-cell empty" />
              ))}

              {/* 날짜 셀 */}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dateStr = getDateStr(day);
                const dayEvents = getEventsForDate(day);
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDate;
                const dow = (firstDay + day - 1) % 7;

                return (
                  <div key={day}
                    className={`cal-cell ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedDate(isSelected ? null : dateStr)}>
                    <div className={`cal-day-num ${dow === 0 ? 'sun' : dow === 6 ? 'sat' : ''}`}>
                      {day}
                    </div>
                    <div className="cal-events-list">
                      {dayEvents.slice(0, 3).map(ev => (
                        <div key={ev.id} className="cal-event-chip"
                          style={{ background: ev.color + '22', color: ev.color, borderLeft: `3px solid ${ev.color}` }}>
                          {ev.is_global && '🌐 '}{ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="cal-event-more">+{dayEvents.length - 3}개</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 하단 - 선택된 날짜 일정 목록 */}
        {selectedDate && (
          <div className="cal-day-panel">
            <div className="cal-day-panel-header">
              <span>📋 {selectedDate} 일정 ({selectedEvents.length}개)</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {isLoggedIn && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setAddDate(selectedDate); setShowAdd(true); }}>
                    + 추가
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => setSelectedDate(null)}>✕</button>
              </div>
            </div>
            <div className="cal-day-panel-body">
              {selectedEvents.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>등록된 일정이 없습니다.</div>
              ) : (
                selectedEvents.map(ev => (
                  <div key={ev.id} className="cal-event-detail">
                    <div className="cal-event-dot" style={{ background: ev.color }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {ev.title}
                        {ev.is_global && <span className="cal-global-badge">전체</span>}
                      </div>
                      {ev.content && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{ev.content}</div>}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{ev.author_name}</div>
                    </div>
                    {(isAdmin || ev.author === user?.id) && (
                      <button className="cal-delete-btn" onClick={() => handleDelete(ev.id)}>🗑️</button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* 일정 추가 모달 */}
        {showAdd && (
          <AddEventModal
            date={addDate}
            isAdmin={isAdmin}
            onClose={() => setShowAdd(false)}
            onSuccess={load}
          />
        )}

        {/* 날짜 상세 모달 (모바일용) */}
        {selectedDate && window.innerWidth < 768 && (
          <DayDetailModal
            date={selectedDate}
            events={selectedEvents}
            onClose={() => setSelectedDate(null)}
            onAdd={() => { setAddDate(selectedDate); setShowAdd(true); }}
            onDelete={handleDelete}
            isAdmin={isAdmin}
            userId={user?.id || null}
          />
        )}
      </div>
    </Layout>
  );
};

export default CalendarPage;
